import type { CostAccountingResponse, SyncStatusResult } from '../types/cost-accounting.js';
import { config } from '../config.js';

/** @graph-connects /cost-accounting/calculate [calls] Cost accounting API via fetch */
async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${config.apiUrl}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...Object.fromEntries(Object.entries(options.headers ?? {})),
    },
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`HTTP ${String(response.status)}`);
  }
  return response.json() as Promise<T>;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business Cost accounting API client. Computes depreciation, disposal, and sale costs for mens items by target month. Also provides sync status and raw data access.
 * @graph-connects /cost-accounting/calculate [calls] Monthly cost accounting calculation
 * @graph-connects /cost-accounting/sync-status [calls] BQ table sync status
 * @graph-connects /cost-accounting/raw-data [calls] Raw data for CSV export
 */
export const costAccountingApi = {
  calculate: (month: string): Promise<CostAccountingResponse> =>
    request<CostAccountingResponse>('/cost-accounting/calculate', {
      method: 'POST',
      body: JSON.stringify({ month }),
    }),

  syncStatus: (): Promise<SyncStatusResult> =>
    request<SyncStatusResult>('/cost-accounting/sync-status'),

  rawData: (type: 'items' | 'purchased' | 'unknown'): Promise<{ type: string; rows: unknown[] }> =>
    request<{ type: string; rows: unknown[] }>(`/cost-accounting/raw-data?type=${type}`),
};
